import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const GusSettings = () => {
  const [settings, setSettings] = useState({
    api_key: '',
    api_key_masked: '',
    use_production: false,
    auto_verify_vat: true,
    cache_results: true,
    cache_hours: 24
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Test wyszukiwania
  const [testNip, setTestNip] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/gus/settings');
      const data = response.data;
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Błąd pobierania ustawień GUS:', error);
      setMessage({ type: 'error', text: 'Nie można pobrać ustawień' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const response = await api.post('/gus/settings', {
          api_key: settings.api_key || undefined,
          use_production: settings.use_production,
          auto_verify_vat: settings.auto_verify_vat,
          cache_results: settings.cache_results,
          cache_hours: settings.cache_hours
        });

      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: 'Ustawienia zapisane!' });
        fetchSettings(); // Odśwież
      } else {
        setMessage({ type: 'error', text: data.message || 'Błąd zapisu' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd połączenia z serwerem' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setMessage(null);

    try {
      const response = await api.post('/gus/test-connection');

      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd połączenia z serwerem' });
    } finally {
      setTesting(false);
    }
  };

  const handleTestSearch = async () => {
    if (!testNip.trim()) {
      setMessage({ type: 'error', text: 'Podaj NIP do wyszukania' });
      return;
    }

    setSearching(true);
    setTestResult(null);
    setMessage(null);

    try {
      const response = await api.get(`/gus/search/nip/${testNip.replace(/[\s-]/g, '')}`);
      const data = response.data;

      if (data.success) {
        setTestResult(data);
        setMessage({ type: 'success', text: 'Znaleziono podmiot!' });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Błąd wyszukiwania' });
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Ładowanie...</div>;
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.5rem' }}>🏛️</span>
        Integracja GUS (REGON) + VAT
      </h2>

      {message && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
          color: message.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Ustawienia API */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          Ustawienia API GUS
        </h3>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Klucz API */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Klucz API (User Key)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={settings.api_key || ''}
                onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                placeholder={settings.api_key_masked || 'Wprowadź klucz API...'}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '0.25rem'
                }}
              />
              <button
                onClick={() => setShowApiKey(!showApiKey)}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg)',
                  cursor: 'pointer'
                }}
              >
                {showApiKey ? '🙈' : '👁️'}
              </button>
            </div>
            <small style={{ color: 'var(--text-muted)', marginTop: '0.25rem', display: 'block' }}>
              Klucz testowy: abcde12345abcde12345 | Produkcyjny: uzyskaj z GUS
            </small>
          </div>

          {/* Środowisko */}
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={settings.use_production}
                onChange={(e) => setSettings({ ...settings, use_production: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Użyj środowiska produkcyjnego</span>
            </label>
            <small style={{ color: 'var(--text-muted)', marginLeft: '1.75rem' }}>
              {settings.use_production
                ? '⚠️ Korzystasz z produkcyjnego API GUS'
                : '🧪 Korzystasz z testowego API GUS'}
            </small>
          </div>

          {/* Auto weryfikacja VAT */}
          <div>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={settings.auto_verify_vat}
                onChange={(e) => setSettings({ ...settings, auto_verify_vat: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Automatycznie sprawdzaj status VAT</span>
            </label>
            <small style={{ color: 'var(--text-muted)', marginLeft: '1.75rem' }}>
              Przy każdym wyszukaniu sprawdzi status na białej liście VAT
            </small>
          </div>

          {/* Przyciski */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--success)', color: 'var(--success)' ,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Zapisywanie...' : '💾 Zapisz ustawienia'}
            </button>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: testing ? 'not-allowed' : 'pointer',
                opacity: testing ? 0.7 : 1
              }}
            >
              {testing ? 'Testowanie...' : '🔌 Testuj połączenie'}
            </button>
          </div>
        </div>
      </div>

      {/* Test wyszukiwania */}
      <div style={{
        backgroundColor: "var(--card-bg)",
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          🔍 Test wyszukiwania
        </h3>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={testNip}
            onChange={(e) => setTestNip(e.target.value)}
            placeholder="Wprowadź NIP (np. 1234563218)"
            style={{
              flex: 1,
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text)',
              fontSize: '12px'
            }}
          />
          <button
            onClick={handleTestSearch}
            disabled={searching}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--info)', color: 'var(--info)' ,
              cursor: searching ? 'not-allowed' : 'pointer'
            }}
          >
            {searching ? 'Szukam...' : '🔍 Szukaj'}
          </button>
        </div>

        {testResult && (
          <div style={{
            backgroundColor: 'var(--bg)',
            padding: '1rem',
            borderRadius: '0.25rem',
            border: '1px solid var(--border)'
          }}>
            <h4 style={{ marginBottom: '0.75rem' }}>Wynik wyszukiwania:</h4>

            {testResult.data && (
              <div className="admin-form-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div><strong>Nazwa:</strong> {testResult.data.nazwa}</div>
                <div><strong>NIP:</strong> {testResult.data.nip}</div>
                <div><strong>REGON:</strong> {testResult.data.regon}</div>
                <div><strong>Miasto:</strong> {testResult.data.miasto}</div>
                <div><strong>Ulica:</strong> {testResult.data.adres}</div>
                <div><strong>Kod pocztowy:</strong> {testResult.data.kod_pocztowy}</div>
              </div>
            )}

            {testResult.vat_status && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: testResult.vat_status.active ? 'var(--success-light)' : 'var(--danger-light)',
                borderRadius: '0.25rem'
              }}>
                <strong>Status VAT:</strong>{' '}
                {testResult.vat_status.active ? (
                  <span style={{ color: 'var(--success)' }}>✅ Czynny podatnik VAT</span>
                ) : (
                  <span style={{ color: 'var(--danger)' }}>❌ {testResult.vat_status.status}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Informacje */}
      <div style={{
        backgroundColor: 'var(--info-light)',
        border: '1px solid var(--info-light)',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <h4 style={{ marginBottom: '0.5rem' }}>ℹ️ Informacje</h4>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', lineHeight: '1.6' }}>
          <li><strong>GUS BIR1</strong> - Baza REGON (dane firm: nazwa, adres, NIP, REGON)</li>
          <li><strong>Biała lista VAT</strong> - Ministerstwo Finansów (status VAT, numery kont bankowych)</li>
          <li>Klucz testowy działa tylko ze środowiskiem testowym GUS</li>
          <li>Klucz produkcyjny uzyskasz wysyłając email na: regon_bir@stat.gov.pl</li>
        </ul>
      </div>
    </div>
  );
};

export default GusSettings;
